// Curated picks for the front-page gallery — six visually distinct cards
// spanning procedures, attack stages, and injects.
const GALLERY: { src: string; title: string; kind: "procedure" | "stage" | "inject" }[] = [
  { src: "/art/cards/1.0.0/proc.network-threat-hunting.png", title: "Network Threat Hunting", kind: "procedure" },
  { src: "/art/cards/1.0.0/ic.phishing.png", title: "Phishing", kind: "stage" },
  { src: "/art/cards/1.0.0/pe.kerberoasting.png", title: "Kerberoasting", kind: "stage" },
  { src: "/art/cards/1.0.0/p.ad-backdoor.png", title: "AD Backdoor", kind: "stage" },
  { src: "/art/cards/1.0.0/proc.cyber-deception.png", title: "Cyber Deception", kind: "procedure" },
  { src: "/art/cards/1.0.0/inj.exec-pressure.png", title: "Executive Pressure", kind: "inject" },
];

// Sorted by tier (L1 → L3) then alphabetical within tier. The catalogue is
// open-ended — new scenarios land regularly. Keep this ordering invariant
// when adding more.
const THEMES: { title: string; tier: "L1" | "L2" | "L3"; minutes: number; body: string; trains: string }[] = [
  // ── L1 ──────────────────────────────────────────────────────────
  {
    title: "Business Email Compromise",
    tier: "L1",
    minutes: 30,
    body: "A wire goes the wrong way. The mailbox is talking on someone's behalf. Counsel is in the room before you finish your coffee.",
    trains: "Mailbox forensics · OAuth audit · comms · legal coordination",
  },
  {
    title: "Cloud Misconfiguration Drift",
    tier: "L1",
    minutes: 30,
    body: "A bucket policy changed last quarter for a one-off integration. It never changed back. A researcher emailed Friday afternoon with a screenshot.",
    trains: "Cloud audit · IAM hygiene · disclosure response · blast-radius scoping",
  },
  {
    title: "Ransomware-shaped Saturday",
    tier: "L1",
    minutes: 30,
    body: "RDP, an inherited acquired-company host, an encryption clock, and peak shipping operations.",
    trains: "Containment · isolation · stakeholder comms · time-boxed decisions",
  },
  // ── L2 ──────────────────────────────────────────────────────────
  {
    title: "External Web Compromise",
    tier: "L2",
    minutes: 45,
    body: "A neglected public-facing service has a passenger. Now the back office can see what the front office can.",
    trains: "Web/server triage · LotL detection · network hunting · containment",
  },
  {
    title: "Identity & Kerberoasting",
    tier: "L2",
    minutes: 45,
    body: "Stolen credentials, ticket-cracked service accounts, and a domain controller volunteering more than it should.",
    trains: "UEBA · SIEM · AD hygiene · eviction tactics",
  },
  {
    title: "Insider & Cloud Exfil",
    tier: "L2",
    minutes: 45,
    body: "A clean offboarding wasn't. Two weeks later a bucket egresses gigabytes to a place it shouldn't.",
    trains: "Identity audit · cloud token review · UEBA · scope investigation",
  },
  // ── L3 ──────────────────────────────────────────────────────────
  {
    title: "OT / ICS Incursion",
    tier: "L3",
    minutes: 60,
    body: "A historian alert nobody calibrated. A maintenance laptop that arrived with a USB stick that came from somewhere. The plant manager is not interested in your OSI layers.",
    trains: "OT/IT segmentation · vendor escalation · safety-vs-containment trade-offs",
  },
  {
    title: "Supply-Chain APT",
    tier: "L3",
    minutes: 60,
    body: "A trusted vendor pushed a tainted update. The driver is signed. The implant is patient. The blast radius is unclear.",
    trains: "Memory forensics · EDR tuning · threat intel · cross-team coordination",
  },
];

interface Props {
  onOpenMenu: (tab: "host" | "join" | "im") => void;
}

export function Landing({ onOpenMenu }: Props) {
  return (
    <>
      <section className="hero">
        <div className="eyebrow">Incident response · live-fire · multiplayer</div>
        <h1>
          Train the way you'll <span className="accent">respond</span>.
        </h1>
        <p>
          Breakpoint is a multiplayer cyber incident-response range. A hidden 4-card attack chain.
          Multiple blue teams, one Incident Master, ten turns. Every roll is a real IR muscle being
          trained — not a runbook being read.
        </p>
        <div className="hero-cta">
          <button className="primary" onClick={() => onOpenMenu("host")}>▶ Host a session</button>
          <button onClick={() => onOpenMenu("join")}>Join as player</button>
          <button onClick={() => onOpenMenu("im")}>Connect as IM</button>
        </div>
        <div className="grid">
          <div>
            <strong>Multi-team race</strong>up to 8 teams · 6 players each
          </div>
          <div>
            <strong>Authoritative server</strong>real-time, replay-deterministic
          </div>
          <div>
            <strong>Trained skills</strong>SIEM · EDR · DNS · UEBA · comms
          </div>
        </div>
      </section>

      <section className="gallery">
        <div className="gallery-head">
          <h2 style={{ margin: 0 }}>The deck</h2>
          <span className="gallery-meta">
            54 cards · 14 procedures · 32 attack stages · 8 injects · AI-rendered, prompt-curated
          </span>
        </div>
        <div className="gallery-strip">
          {GALLERY.map((g) => (
            <figure key={g.src} className={`gallery-card ${g.kind}`}>
              <img src={g.src} alt="" loading="lazy" />
              <figcaption>
                <span className="kind">
                  {g.kind === "procedure" ? "Procedure" : g.kind === "stage" ? "Attack" : "Inject"}
                </span>
                <span className="title">{g.title}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="panel" style={{ marginBottom: 22 }}>
        <h2>What you'll train</h2>
        <p style={{ fontSize: 13, color: "var(--color-text-muted)", lineHeight: 1.6, marginBottom: 8, maxWidth: 720 }}>
          A growing catalogue of scenario shapes drawn from real incident-response patterns. Each
          one ships with a composite victim org, named characters with stock lines, scripted
          injects, and an AAR template — so the IM walks in with everything they need to run a
          credible exercise. Specific scenario assignments stay sealed until teams join.
        </p>
        <p style={{ fontSize: 12, color: "var(--color-text-subtle)", lineHeight: 1.55, marginBottom: 16, maxWidth: 720, fontStyle: "italic" }}>
          New scenarios are added regularly. The shapes below are a sample of what's currently in
          rotation. Got a shape we're missing?{" "}
          <a
            href={
              "mailto:scenarios@breakpointhq.co?subject=" +
              encodeURIComponent("Scenario suggestion") +
              "&body=" +
              encodeURIComponent(
                [
                  "Shape: (e.g. ransomware-shaped Saturday, supply-chain APT, OT/ICS, exec travel, ...)",
                  "",
                  "Composite victim org (1 line, no real victims):",
                  "",
                  "Premise (3-5 lines, what wakes the SOC at 02:14):",
                  "",
                  "What this trains (procedures it exercises, skills players grow):",
                  "",
                  "Anything else (real-world inspiration, references, source links):",
                ].join("\n"),
              )
            }
            style={{ color: "var(--color-accent)", borderBottom: "1px solid var(--color-accent-dim)" }}
          >
            email scenarios@breakpointhq.co
          </a>{" "}
          with the shape, the org composite, and what it should train.
        </p>
        <div className="theme-grid">
          {THEMES.map((t) => (
            <article key={t.title} className="theme-tile">
              <span className="tier-chip">{t.tier}</span>
              <span className="tier-time">{t.minutes} min</span>
              <h3 className="theme-title">{t.title}</h3>
              <p className="theme-body">{t.body}</p>
              <div className="theme-trains">
                <span className="label">Trains</span>
                {t.trains}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="cta-strip">
        <div className="cta-eyebrow">▌ Live-fire range, on demand</div>
        <h2>Ready to run a drill?</h2>
        <p>
          A session takes 30–60 minutes. Two browsers and a quiet room are enough to start.
        </p>
        <div className="actions">
          <button className="primary" onClick={() => onOpenMenu("host")}>▶ Host a session</button>
          <button onClick={() => onOpenMenu("join")}>Join as player</button>
        </div>
      </section>
    </>
  );
}
